import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "form",
    "submitButton",
    "errorMessage",
    "currentCredits",
    "requiredCredits",
  ];
  static values = { statusUrl: String };

  connect() {
    this.modals = {
      prompt: this.initModal("#promptInput"),
      loading: this.initModal("#loadingModal"),
      credits: this.initModal("#insufficientCreditsModal"),
    };
    this.isSubmitting = false;
    this.pollingInterval = null;
  }

  async submitForm(event) {
    event.preventDefault();
    if (this.isSubmitting) return;

    try {
      this.isSubmitting = true;
      this.disableForm();

      this.hideModal("prompt");

      const response = await fetch(this.formTarget.action, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            .content,
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(new FormData(this.formTarget)),
      });

      const data = await this.handleResponse(response);
      if (data?.id) await this.pollImageStatus(data.id);
    } catch (error) {
      await this.handleError(error);
    } finally {
      this.isSubmitting = false;
      this.enableForm();
      this.cleanupModals();
    }
  }

  async handleResponse(response) {
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw {
        status: response.status,
        message: data.error || "Request failed",
        current_credits: data.current_credits,
        required_credits: data.required_credits,
      };
    } else {
      this.showModal("loading");
    }
    return data;
  }

  async pollImageStatus(id) {
    let attempts = 0;
    const maxAttempts = 500; // 30 minutes (3500ms * 500)

    const checkStatus = async () => {
      try {
        const response = await fetch(`${this.statusUrlValue}/${id}/status`);
        const data = await response.json();

        if (data.redirect_url) {
          window.location.href = data.redirect_url;
          return true;
        } else if (data.status === "failed") {
          throw new Error(data.error_message || "Image generation failed");
        }
      } catch (error) {
        console.error("Error checking status:", error);
        throw error;
      }

      if (++attempts >= maxAttempts) {
        throw new Error("Timeout: Image generation took too long");
      }

      return false;
    };

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const isDone = await checkStatus();
          if (isDone) {
            resolve();
          } else {
            setTimeout(poll, 3500);
          }
        } catch (error) {
          reject(error);
        }
      };

      poll();
    });
  }

  showSuccessMessage(creditsUsed) {
    const flashMessage = document.createElement("div");
    flashMessage.className = "alert alert-success alert-dismissible fade show";
    flashMessage.setAttribute("role", "alert");
    flashMessage.innerHTML = `
      Successfully generated image ad. Credits utilized: ${creditsUsed}
      <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
    `;

    // Insert at the top of the content
    const container = document.querySelector(".container");
    if (container) {
      container.insertBefore(flashMessage, container.firstChild);
    }

    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      flashMessage.remove();
    }, 5000);
  }

  async handleError(error) {
    console.error("Error:", error);
    this.hideModal("loading");

    if ([422, 402].includes(error?.status)) {
      this.showCreditsError({
        error: error.message || "Insufficient credits",
        current_credits: error.current_credits ?? "N/A",
        required_credits: error.required_credits ?? "N/A",
      });
    } else {
      alert(error.message || "An unexpected error occurred");
      console.log(error);
    }
  }

  showCreditsError(data) {
    this.errorMessageTarget.textContent = data.error;
    this.currentCreditsTarget.textContent = data.current_credits;
    this.requiredCreditsTarget.textContent = data.required_credits;
    this.showModal("credits");
  }

  initModal(selector) {
    const element = document.querySelector(selector);
    return element ? new bootstrap.Modal(element) : null;
  }

  cleanupModals() {
    // Remove Bootstrap backdrop
    const backdrop = document.querySelector(".modal-backdrop");
    if (backdrop) backdrop.remove();

    // Reset body styles
    document.body.classList.remove("modal-open");
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
  }

  showModal(name) {
    if (this.modals[name]) this.modals[name].show();
  }

  hideModal(name) {
    if (this.modals[name]) this.modals[name].hide();
  }

  disableForm() {
    this.submitButtonTarget.disabled = true;
    this.submitButtonTarget.style.opacity = "0.5";
  }

  enableForm() {
    this.submitButtonTarget.disabled = false;
    this.submitButtonTarget.style.opacity = "1";
  }
}
